<template>
  <div class="d-comment-box comment-item grid-flex grid-no-response">
    <div class="grid-item-shrink avatar">
      <img :src="userAvatarUrl">
    </div>

    <div class="grid-item">
      <form
        :class="{ 'has-content': (!!comment && !!comment.length),
                  'has-focus': isFocused }"
        @submit.stop.prevent="submitComment"
      >
        <DTextCompose
          ref="commentText"
          v-model="comment"
          :auto-focus="autoFocus"
          :disabled="isLoading"
          :mentions="mentions"
          :placeholder="placeholder"
          :value="value"
          @blur="isFocused = false"
          @focus="isFocused = true"
          @meta-enter="submitComment"
        />

        <div class="actions text-right">
          <small class="text-grey-dark space-right-1em hidden-until-md">
            Cmd/Ctrl + Enter to
            <span v-if="commentSlug">save</span>
            <span v-else>post</span>
          </small>

          <button
            class="button-yellow button-follow button-sm"
            :disabled="isLoading"
            type="submit"
          >
            <span v-if="isLoading">
              <span v-if="commentSlug">Saving...</span>
              <span v-else>Posting...</span>
              <DuiLoadingIndicator size="sm space-left-1em" />
            </span>
            <span v-else>
              <span v-if="commentSlug">Save</span>
              <span v-else>Post</span>
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import DTextCompose from '@d-components/d-text-compose/d-text-compose.vue';
import commentsService from '@d-services/comments-service/comments-service';
import { getErrorMessage } from '@d-shared/utils/errors';

export default {
  components: {
    DTextCompose,
  },
  props: {
    autoFocus: {
      type: Boolean,
      default: false,
    },
    commentSlug: {
      type: String,
      default: null,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book',
          'post',
          'user-review',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
    mentions: {
      type: Array,
      default: null,
    },
    parentId: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Write a comment...',
    },
    userAvatarUrl: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    comment: null,
    isFocused: false,
    isLoading: false,
  }),
  watch: {
    value: function() {
      this.setInitialValue();
    },
  },
  mounted() {
    this.setInitialValue();
  },
  methods: {
    focus() {
      this.$refs.commentText.focus();
    },

    setInitialValue() {
      if (!this.value) {
        return;
      }

      this.$set(this, 'comment', this.value);
      this.$refs.commentText.setEditorContentFromText(this.comment);
    },

    async submitComment() {
      if (!this.comment) {
        return;
      }

      this.$set(this, 'isLoading', true);

      try {
        if (this.commentSlug) {
          await this.submitEditedComment();
        } else {
          await this.submitNewComment();
        }

        this.$set(this, 'isFocused', false);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },

    async submitEditedComment() {
      const mentions = this.$refs.commentText.getMentions();

      let submittedComment;

      switch (this.itemType) {
      case 'book':
        submittedComment = await commentsService.updateBookComment(this.itemSlug, this.commentSlug, this.comment, mentions);
        break;
      case 'post':
        submittedComment = await commentsService.updatePostComment(this.itemSlug, this.commentSlug, this.comment, mentions);
        break;
      case 'user-review':
        submittedComment = await commentsService.updateUserReviewComment(this.itemSlug, this.commentSlug, this.comment, mentions);
        break;
      }

      this.$refs.commentText.clear();
      this.$emit('comment-edited', submittedComment);
      Vue.duiNotificationManager.success('Comment updated!');
    },

    async submitNewComment() {
      const mentions = this.$refs.commentText.getMentions();

      let submittedComment;

      switch (this.itemType) {
      case 'book':
        submittedComment = await commentsService.postBookComment(this.itemSlug, this.comment, mentions, this.parentId);
        break;
      case 'post':
        submittedComment = await commentsService.postPostComment(this.itemSlug, this.comment, mentions, this.parentId);
        break;
      case 'user-review':
        submittedComment = await commentsService.postUserReviewComment(this.itemSlug, this.comment, mentions, this.parentId);
        break;
      }

      this.$refs.commentText.clear();
      this.$emit('comment-added', submittedComment);
      Vue.duiNotificationManager.success('Comment added!');
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

$textarea-content-height: $_10em;

.d-comment-box {
  .avatar {
    margin-right: $_1em !important;
  }

  form {
    background-color: getColor($white);
    border: $border-width-base solid getColor($grey, medium);
    border-radius: $border-radius-lg;
    position: relative;

    @include transition((
      border-color
    ));

    .d-text-compose {
      border-radius: $border-radius-lg;

      .ql-container {
        padding: $_1em 0 0 $_1em;

        .ql-editor {
          font-size: $font-size-sm;
          font-family: $base-font;
          height: 30px;
          min-height: 30px;

          &:before {
            left: $_1em;
          }

          @include screenMoreThan(sm) {
            font-size: $font-size-base;
          }

          @include transition((
            min-height
          ));
        }
      }
    }

    .actions {
      display: none;
      margin: 0 $_1em $_1em 0;
      opacity: 0;

      @include transition((
        opacity
      ));

      .dui-loading-indicator {
        margin-top: -($_0-5em);
      }
    }

    &:hover,
    &.has-focus {
      border-color: getColor($yellow);
    }

    &.has-content,
    &.has-focus {
      .d-text-compose .ql-container .ql-editor {
        height: initial;
        min-height: $textarea-content-height;
      }

      .actions {
        display: block;
      }
    }

    &.has-content .actions {
      opacity: 1;
    }
  }
}
</style>
